import React, { useEffect, useMemo, useState } from "react";

import { AlertV4, ButtonV4, ChipV4, FieldV4, PaginationV4, SelectV4 } from "../common/ui";

import ModalCancelAppointmentV4 from "./ModalCancelAppointmentV4";
import {
  ALL,
  APPOINTMENT_STATUS_OPTIONS,
  CONFIRM_TYPE,
  LIST_ACTIONS,
  LIST_BTN,
  LIST_ERROR_KEY,
  LIST_SEQUENCE,
  PATIENT_FILTER_SEARCH,
  ROW_ACTION_TEXT,
  SERVICE_STATUS_OPTIONS,
  emptyListFilter,
  finishItemsOf,
  personName,
  providerOptions,
  searchKeyOf,
  toBeDateText,
  toListRow,
  type ApiRecord,
  type AppointmentListSlice,
  type CancelType,
  type ListRow,
  type ProviderRecord,
  type RowAction,
} from "./appointmentModelV4";
import type {
  AppointmentRunSequence,
  AppointmentSetProp,
  ItemSearchEvent,
} from "./CardAppointmentV4";

type AppointmentListV4Props = {
  list?: AppointmentListSlice | null;
  providers?: ProviderRecord[];
  searchedItemListWithKey?: Record<string, unknown>;
  buttonLoadCheck?: Record<string, unknown>;
  errorMessage?: Record<string, unknown>;
  runSequence: AppointmentRunSequence;
  setProp?: AppointmentSetProp;
  onEvent?: (event: ItemSearchEvent) => void;
  createdIds?: (number | string)[];
  onResult: (text: string) => void;
};

type FilterKey = keyof ReturnType<typeof emptyListFilter>;

const FILTER_PATH = "AppointmentListMEHSequence.filterAppointmentList";

const AppointmentListV4 = (props: AppointmentListV4Props) => {
  const [dateText, setDateText] = useState({ startDate: "", endDate: "" });
  const [dateError, setDateError] = useState(false);
  const [patientText, setPatientText] = useState("");
  const [patientOpen, setPatientOpen] = useState(false);
  const [checked, setChecked] = useState<(number | string)[]>([]);
  const [menuId, setMenuId] = useState<number | string | null>(null);
  const [cancelRow, setCancelRow] = useState<ListRow | null>(null);

  const filter = { ...emptyListFilter(), ...props.list?.filterAppointmentList };
  const page = props.list?.appointmentListSummaryPage;

  const searching = props.buttonLoadCheck?.[LIST_BTN.SEARCH] === "LOADING";
  const saving =
    props.buttonLoadCheck?.[LIST_BTN.CONFIRM_APPOINTMENT] === "LOADING" ||
    props.buttonLoadCheck?.[LIST_BTN.CONFIRM_APPOINTMENT_FINISH] === "LOADING";

  const error = props.errorMessage?.[LIST_ERROR_KEY];

  const patientResult = props.searchedItemListWithKey?.[searchKeyOf(PATIENT_FILTER_SEARCH)] as
    ApiRecord[] | undefined;
  const patients = patientResult ?? [];

  // ยังไม่ได้ผลค้นหาแรก = กำลังโหลด
  const loading = searching || (props.list?.appointmentList === undefined && !error);

  // Memo
  const rows = useMemo(
    () => (props.list?.appointmentList ?? []).map((item) => toListRow(item as ApiRecord)),
    [props.list?.appointmentList],
  );

  // Effect
  useEffect(() => {
    setChecked([]);
    setMenuId(null);
  }, [props.list?.appointmentList]);

  // Handler
  const handleCloseError = () => {
    props.setProp?.(`errorMessage.${LIST_ERROR_KEY}`, null);
  };

  const handleChangeFilter = (name: FilterKey, value: unknown, callback?: () => void) => {
    if (props.setProp) {
      props.setProp(`${FILTER_PATH}.${name}`, value, callback);
    } else {
      callback?.();
    }
  };

  const handleSearchPage = (activePage?: number) => {
    setChecked([]);
    setMenuId(null);

    props.runSequence({
      sequence: LIST_SEQUENCE,
      action: LIST_ACTIONS.SEARCH,
      ...(activePage ? { activePage } : {}),
    });
  };

  const handleSearch = () => {
    const invalid = [dateText.startDate, dateText.endDate].some(
      (text) => toBeDateText(text) === null,
    );

    setDateError(invalid);

    if (!invalid) {
      handleSearchPage();
    }
  };

  const handleClear = () => {
    setDateText({ startDate: "", endDate: "" });
    setDateError(false);
    setPatientText("");
    setPatientOpen(false);

    if (props.setProp) {
      props.setProp(FILTER_PATH, emptyListFilter(), () => handleSearchPage());
    } else {
      handleSearchPage();
    }
  };

  const handleDate = (name: "startDate" | "endDate", text: string) => {
    setDateText({ ...dateText, [name]: text });

    const value = toBeDateText(text);

    if (value !== null) {
      handleChangeFilter(name, value);
    }
  };

  const handlePatientSearch = () => {
    handleChangeFilter("patientID", "");

    if (!patientText.trim()) {
      setPatientOpen(false);

      return;
    }

    props.onEvent?.({
      message: "ItemSearch",
      params: {
        ...PATIENT_FILTER_SEARCH,
        searchText: patientText.trim(),
        limit: 20,
        urlParams: { active: true },
      },
    });

    setPatientOpen(true);
  };

  const handlePatientPick = (item: ApiRecord) => {
    setPatientText(personName(item));
    setPatientOpen(false);

    handleChangeFilter("patientID", item.id);
  };

  const handleChangePatientText = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPatientText(e.target.value);

    if (!e.target.value) {
      setPatientOpen(false);
      handleChangeFilter("patientID", "");
    }
  };

  const handlePatientKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handlePatientSearch();
    }
  };

  const handleChangeProvider = (value: string) => {
    handleChangeFilter("providerID", value === ALL ? ALL : Number(value));
  };

  const handleToggleMenu = (row: ListRow) => {
    setMenuId(menuId === row.id ? null : row.id);
  };

  const handleToggleChecked = (row: ListRow) => {
    setChecked(
      checked.includes(row.id) ? checked.filter((id) => id !== row.id) : [...checked, row.id],
    );
  };

  /** ตั้ง appointmentDetail ของ sequence แล้วค่อยเรียก CONFIRM_APPOINTMENT (sequence อ่านจาก state) */
  const handleConfirm = (detail: ApiRecord, type: string, onDone: () => void) => {
    const params = {
      sequence: LIST_SEQUENCE,
      action: LIST_ACTIONS.CONFIRM_APPOINTMENT,
      data: { type },
      callback: onDone,
    };

    if (props.setProp) {
      props.setProp("AppointmentListMEHSequence.appointmentDetail", { ...detail, type }, () => {
        props.runSequence(params);
      });
    } else {
      props.runSequence(params);
    }
  };

  const handleAction = (row: ListRow, action: RowAction) => {
    setMenuId(null);

    if (action === "cancel") {
      handleCloseError();
      props.onResult("");
      setCancelRow(row);

      return;
    }

    const text = {
      confirm: `ยืนยันนัดหมาย ${row.id} แล้ว`,
      finish: `บันทึกรับบริการแล้ว ${row.id}`,
      postpone: `เลื่อนนัด ${row.id} แบบไม่ระบุวัน · เปิดกล่อง "แสดงนัดหมายที่ถูกเลื่อน" เพื่อดู`,
    }[action];

    handleConfirm(row.raw, CONFIRM_TYPE[action], () => props.onResult(text));
  };

  const handleFinish = () => {
    const items = finishItemsOf(rows, checked);

    if (!items.length) {
      return;
    }

    const count = checked.length;

    const params = {
      sequence: LIST_SEQUENCE,
      action: LIST_ACTIONS.CONFIRM_APPOINTMENT_FINISH,
      callback: () => {
        setChecked([]);

        props.onResult(`บันทึก "รับบริการแล้ว" ${count} รายการ`);
      },
    };

    if (props.setProp) {
      props.setProp("AppointmentListMEHSequence.appointmentFinish", items, () => {
        props.runSequence(params);
      });
    } else {
      props.runSequence(params);
    }
  };

  const handleCloseCancel = () => {
    setCancelRow(null);

    props.setProp?.("AppointmentListMEHSequence.appointmentDetail", {});
  };

  const handleConfirmCancel = (row: ListRow, type: CancelType, reason: string) => {
    handleConfirm({ ...row.raw, statusNote: reason }, type, () => {
      setCancelRow(null);

      props.onResult(`ยกเลิกนัดหมาย ${row.id} แล้ว`);
    });
  };

  // Render
  return (
    <>
      <section className="hv4-appt-box hv4-appt-filter" aria-label="ตัวกรองนัดหมาย">
        <FieldV4 label="BookingID" htmlFor="hv4-appt-id">
          <input
            id="hv4-appt-id"
            className="hv4-input"
            value={filter.id ?? ""}
            onChange={(e) => handleChangeFilter("id", e.target.value)}
          />
        </FieldV4>
        <FieldV4 label="ชื่อ-นามสกุลผู้รับบริการ" htmlFor="hv4-appt-patient">
          <div className="hv4-appt-search">
            <input
              id="hv4-appt-patient"
              className="hv4-input"
              value={patientText}
              onChange={handleChangePatientText}
              onKeyDown={handlePatientKeyDown}
            />
            {patientOpen && (
              <ul className="hv4-appt-picks" role="listbox" aria-label="ผลค้นหาผู้รับบริการ">
                {patients.length ? (
                  patients.map((item) => (
                    <li
                      key={item.id}
                      role="option"
                      aria-selected={filter.patientID === item.id}
                      className="hv4-appt-pick"
                      onClick={() => handlePatientPick(item)}
                    >
                      {personName(item)}
                    </li>
                  ))
                ) : (
                  <li className="hv4-appt-picks-empty">
                    {patientResult === undefined ? "กำลังค้นหา" : "ไม่พบผู้รับบริการ"}
                  </li>
                )}
              </ul>
            )}
          </div>
        </FieldV4>
        <FieldV4 label="วันที่นัดหมาย (จาก)" htmlFor="hv4-appt-from">
          <input
            id="hv4-appt-from"
            className="hv4-input"
            placeholder="วว/ดด/ปปปป"
            value={dateText.startDate}
            aria-invalid={dateError && toBeDateText(dateText.startDate) === null}
            onChange={(e) => handleDate("startDate", e.target.value)}
          />
        </FieldV4>
        <FieldV4 label="วันที่นัดหมาย (ถึง)" htmlFor="hv4-appt-to">
          <input
            id="hv4-appt-to"
            className="hv4-input"
            placeholder="วว/ดด/ปปปป"
            value={dateText.endDate}
            aria-invalid={dateError && toBeDateText(dateText.endDate) === null}
            onChange={(e) => handleDate("endDate", e.target.value)}
          />
        </FieldV4>
        <FieldV4 label="ผู้ให้บริการ" htmlFor="hv4-appt-provider">
          <SelectV4
            id="hv4-appt-provider"
            options={providerOptions(props.providers, true)}
            value={String(filter.providerID ?? ALL)}
            onChange={handleChangeProvider}
          />
        </FieldV4>
        <FieldV4 label="สถานะนัดหมาย" htmlFor="hv4-appt-status">
          <SelectV4
            id="hv4-appt-status"
            options={APPOINTMENT_STATUS_OPTIONS}
            value={filter.appointmentStatus ?? ALL}
            onChange={(value) => handleChangeFilter("appointmentStatus", value)}
          />
        </FieldV4>
        <FieldV4 label="สถานะรับบริการ" htmlFor="hv4-appt-service">
          <SelectV4
            id="hv4-appt-service"
            options={SERVICE_STATUS_OPTIONS}
            value={filter.orderStatus ?? ALL}
            onChange={(value) => handleChangeFilter("orderStatus", value)}
          />
        </FieldV4>
        <div className="hv4-appt-filter-foot">
          <label className="hv4-appt-check">
            <input
              type="checkbox"
              checked={!!filter.noDivisionServiceBlock}
              onChange={(e) => handleChangeFilter("noDivisionServiceBlock", e.target.checked)}
            />
            แสดงนัดหมายที่ถูกเลื่อนแบบไม่ระบุวัน
          </label>
          <ButtonV4 variant="ghost" onClick={handleClear}>
            ล้างตัวกรอง
          </ButtonV4>
          <ButtonV4 className="hv4-appt-btn-brand" disabled={searching} onClick={handleSearch}>
            ค้นหาข้อมูล
          </ButtonV4>
        </div>
        {dateError && (
          <AlertV4 tone="danger" className="hv4-appt-filter-foot">
            วันที่ไม่ถูกต้อง · พิมพ์เป็น วว/ดด/ปปปป (พ.ศ. หรือ ค.ศ.)
          </AlertV4>
        )}
      </section>

      {!cancelRow && <AlertV4 tone="danger" error={error} onClose={handleCloseError} />}

      <section className="hv4-appt-box hv4-appt-list" aria-label="รายชื่อผู้นัดหมาย">
        <div className="hv4-appt-table-head">
          <span className="hv4-appt-table-title">รายชื่อผู้นัดหมาย</span>
          <span className="hv4-appt-table-sub">
            {page?.count !== undefined && `พบ ${page.count} รายการ · `}
            เลือกแถวแล้วกด "รับบริการแล้ว"
          </span>
          <ButtonV4
            variant="primary"
            className="hv4-appt-finish"
            disabled={!checked.length || saving}
            aria-disabled={!checked.length}
            onClick={handleFinish}
          >
            {checked.length ? `รับบริการแล้ว (${checked.length})` : "รับบริการแล้ว"}
          </ButtonV4>
        </div>
        <div className="hv4-appt-scroll">
          <div className="hv4-appt-grid" role="table" aria-label="ตารางนัดหมาย">
            <div className="hv4-appt-row hv4-appt-row--head" role="row">
              <span role="columnheader" />
              <span role="columnheader">BookingID</span>
              <span role="columnheader">ชื่อ-นามสกุล</span>
              <span role="columnheader">เบอร์โทร</span>
              <span role="columnheader">วันเวลาที่นัดหมาย</span>
              <span role="columnheader">ผู้ให้บริการ / บริการ</span>
              <span role="columnheader">สถานะนัดหมาย</span>
              <span role="columnheader">วันที่รับบริการ</span>
              <span role="columnheader">สถานะรับบริการ</span>
              <span role="columnheader" />
            </div>
            {rows.map((row) => (
              <React.Fragment key={row.id}>
                <div
                  className={`hv4-appt-row${props.createdIds?.includes(row.id) ? " hv4-appt-row--new" : ""}`}
                  role="row"
                >
                  <span role="cell">
                    <input
                      type="checkbox"
                      aria-label="เลือกแถวนี้"
                      checked={checked.includes(row.id)}
                      onChange={() => handleToggleChecked(row)}
                    />
                  </span>
                  <span role="cell" className="hv4-appt-id">
                    {row.id}
                  </span>
                  <span role="cell">
                    <div>{row.name}</div>
                    <div className="hv4-appt-sub">{row.hn ? `HN ${row.hn}` : "ยังไม่มี HN"}</div>
                  </span>
                  <span role="cell">{row.tel}</span>
                  <span role="cell">
                    <div>{row.when}</div>
                    {row.insertQueue && <div className="hv4-appt-queue">คิวแทรก</div>}
                  </span>
                  <span role="cell">{row.provider}</span>
                  <span role="cell">
                    <ChipV4 tone={row.statusTone} className="hv4-appt-chip">
                      {row.statusText}
                    </ChipV4>
                  </span>
                  <span role="cell">{row.serviceDate}</span>
                  <span role="cell">
                    <ChipV4 tone={row.serviceTone} className="hv4-appt-chip">
                      {row.serviceText}
                    </ChipV4>
                  </span>
                  <span role="cell">
                    <button
                      type="button"
                      className="hv4-appt-more"
                      aria-label="การทำงาน"
                      aria-expanded={menuId === row.id}
                      onClick={() => handleToggleMenu(row)}
                    >
                      ⋮
                    </button>
                  </span>
                </div>
                {menuId === row.id && (
                  <div className="hv4-appt-menu" role="group" aria-label={`การทำงานของ ${row.id}`}>
                    {row.actions.map((action) => (
                      <button
                        key={action}
                        type="button"
                        className={`hv4-appt-act hv4-appt-act--${action}`}
                        disabled={saving}
                        onClick={() => handleAction(row, action)}
                      >
                        {ROW_ACTION_TEXT[action]}
                      </button>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
            {!rows.length && (
              <div className="hv4-appt-empty">
                {loading ? "กำลังโหลด" : "ไม่พบนัดหมายตามเงื่อนไข"}
              </div>
            )}
          </div>
        </div>
      </section>
      <PaginationV4
        page={page?.activePage || 1}
        pageCount={Number(page?.total) || 1}
        onChange={handleSearchPage}
      />

      {cancelRow && (
        <ModalCancelAppointmentV4
          row={cancelRow}
          busy={saving}
          error={error}
          onClose={handleCloseCancel}
          onConfirm={(type, reason) => handleConfirmCancel(cancelRow, type, reason)}
        />
      )}
    </>
  );
};

AppointmentListV4.displayName = "AppointmentListV4";

export default AppointmentListV4;
